import { useTranslation } from '@whoop/i18n/lang/client';
import { AmplitudeEvents } from 'whoop-analytics/lib/types';
import { useNavigation } from '@/hooks/navigation';
import { useAnalytics } from '@/hooks/analytics';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useShallow } from 'zustand/react/shallow';
import {
  getAccessoriesPageUrl,
  getMembershipPageUrl,
  getOrderPageUrl,
} from '@/lib/utils/navigationHelpers';
import type { CartButtonVariants } from './CartCTAButtons';

export interface UseSharedButtonLogicReturn {
  primaryButtonActionVariants: Partial<Record<CartButtonVariants, () => void>>;
  secondaryButtonActionVariants: Partial<
    Record<CartButtonVariants, () => void>
  >;
  primaryButtonLabelVariants: Record<CartButtonVariants, string>;
  secondaryButtonLabelVariants: Partial<Record<CartButtonVariants, string>>;
  onContinueToCheckout: () => void;
  onContinueToAccessories: () => void;
}

interface UseSharedButtonLogicProps {
  isUpgradeFlow: boolean;
  buttonsVariant: CartButtonVariants;
}

export function useSharedButtonLogic({
  isUpgradeFlow,
  buttonsVariant,
}: UseSharedButtonLogicProps): UseSharedButtonLogicReturn {
  const { t } = useTranslation('cart');
  const { trackAmplitudeEvent } = useAnalytics();
  const { goTo } = useNavigation();
  const { cart } = useSiteStore(
    useShallow((state) => ({
      cart: state.cart,
    })),
  );

  const onContinueToCheckout = async (): Promise<void> => {
    trackAmplitudeEvent(AmplitudeEvents.CloseCart, {
      CTA: 'Check Out',
    });
    const url = getOrderPageUrl({
      isUpgradeFlow,
      cartItemGroups: cart.cartItemGroups,
    });
    goTo(url);
  };

  const onContinueToAccessories = async (): Promise<void> => {
    trackAmplitudeEvent(AmplitudeEvents.CloseCart, {
      CTA:
        buttonsVariant === 'continueToAccessories' ||
        buttonsVariant === 'control'
          ? 'Continue'
          : 'Add Accessories',
    });
    const url = getAccessoriesPageUrl({
      isUpgradeFlow,
      cartItemGroups: cart.cartItemGroups,
    });
    goTo(url);
  };

  const onContinueToMembership = async (): Promise<void> => {
    trackAmplitudeEvent(AmplitudeEvents.CloseCart, {
      CTA: 'Continue',
    });
    goTo(getMembershipPageUrl(cart.cartItemGroups));
  };

  const primaryButtonActionVariants: Partial<
    Record<CartButtonVariants, () => void>
  > = {
    continueToCheckout: onContinueToCheckout,
    continueToAccessories: onContinueToAccessories,
    control: onContinueToAccessories,
    checkoutAndAddAccessories: onContinueToCheckout,
    continueToMembership: onContinueToMembership,
  };

  const secondaryButtonActionVariants: Partial<
    Record<CartButtonVariants, () => void>
  > = {
    checkoutAndAddAccessories: onContinueToAccessories,
  };

  const primaryButtonLabelVariants = {
    continueToCheckout: t('continue'),
    continueToAccessories: t('continue'),
    control: t('continue'),
    checkoutAndAddAccessories: t('checkout'),
    continueToMembership: t('continue'),
    chooseUpgradeOption: t('chooseUpgradeOption'),
    chooseUpcycleOption: t('chooseUpcycleOption'),
  };

  const secondaryButtonLabelVariants: Partial<
    Record<CartButtonVariants, string>
  > = {
    checkoutAndAddAccessories: t('addAccessories'),
  };

  return {
    primaryButtonActionVariants,
    secondaryButtonActionVariants,
    primaryButtonLabelVariants,
    secondaryButtonLabelVariants,
    onContinueToCheckout,
    onContinueToAccessories,
  };
}
